"use client";

import { Loader2, Users, Search, ChevronDown, Check } from "lucide-react";
import { useMemo, useState, useRef, useEffect } from "react";

import { cn } from "@/app/_module/lib/utils";

import { generateInitials } from "../constants/generateinitials";
import { useAgentWorkload } from "../hooks/queries";
import type { AgentWorkloadResponse } from "../types/leadassign-types";

// Compact big counts so both "assigned" and "uploaded" fit on one dropdown row:
// 1500 -> "1.5k", 100000 -> "100k". Under 1,000 stays exact.
const fmtCompact = (n: number | null | undefined): string => {
  const v = Number(n ?? 0);
  if (!isFinite(v)) return "0";
  if (Math.abs(v) < 1000) return String(v);
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(v).toLowerCase();
};

interface AgentAssignmentDropdownProps {
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /**
   * Which way the panel opens. Defaults to 'up' because the original caller is a
   * modal FOOTER, where opening down puts the list off-screen. The import mapping
   * screen has room below, so it passes 'down'.
   */
  openDirection?: 'up' | 'down';
  /**
   * When set, a clear row is rendered at the top of the list with this label
   * (e.g. "Nobody — import unassigned") which selects the empty string.
   * Omit it and the dropdown behaves exactly as before: pick an agent, no clear.
   */
  clearLabel?: string;
}

// Shared trigger styling (matches the shadcn SelectTrigger it replaces).
const triggerBase =
  "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-80";

export const AgentAssignmentDropdown = function({
  value,
  onValueChange,
  placeholder = "Select agent",
  disabled = false,
  className,
  openDirection = 'up',
  clearLabel,
}: AgentAssignmentDropdownProps) {
  const { data: agentWorkloadData, isLoading, error } = useAgentWorkload();
  const [open, setOpen] = useState(false);
  const [agentSearch, setAgentSearch] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const agents = useMemo(() => {
    const agentList = (agentWorkloadData?.agents || []) as any[];
    return agentList;
  }, [agentWorkloadData]);

  const filteredAgents = useMemo(() => {
    const q = agentSearch.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter((agent: any) =>
      (agent.agentName || "").toLowerCase().includes(q) ||
      (agent.agencyName || "").toLowerCase().includes(q)
    );
  }, [agents, agentSearch]);

  const selectedAgent = useMemo(() => {
    return agents.find((agent: any) => agent.agentId === value);
  }, [agents, value]);

  const close = () => {
    setOpen(false);
    setAgentSearch("");
  };

  // Close on a click/tap outside, or Escape. `click`, NOT mousedown/touchstart: on an iPad the list
  // opened below the visible part of the import dialog's scroll area, and the swipe to scroll down
  // to it began with a touchstart outside the panel — so it closed every time and no agent could be
  // picked. A scroll never makes a click. Capture phase: the click that opened it is already past it.
  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        close();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("click", onClickOutside, true);
    document.addEventListener("keydown", onKey);
    panelRef.current?.scrollIntoView?.({ block: "nearest" });
    // Touch screens: no auto-focus — it pops the keyboard, which covers the list it just opened.
    const t = window.matchMedia("(pointer: coarse)").matches
      ? undefined
      : setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("click", onClickOutside, true);
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [open]);

  // Non-interactive states render a disabled-looking trigger, same as before.
  if (isLoading) {
    return (
      <button type="button" disabled className={cn(triggerBase, className)}>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Loading agents...</span>
        </div>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </button>
    );
  }

  if (error) {
    return (
      <button type="button" disabled className={cn(triggerBase, className)}>
        <span className="text-muted-foreground">Error loading agents</span>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </button>
    );
  }

  if (!agents || agents.length === 0) {
    return (
      <button type="button" disabled className={cn(triggerBase, className)}>
        <span className="text-muted-foreground">No agents available</span>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </button>
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      {/* Trigger — looks identical to the old select trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => (open ? close() : setOpen(true))}
        className={cn(triggerBase, className)}
      >
        {selectedAgent ? (
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-[9px] font-semibold text-primary leading-none">
                {generateInitials(selectedAgent.agentName)}
              </span>
            </div>
            <span className="truncate font-medium min-w-0 flex-1 text-left">
              {selectedAgent.agentName}
            </span>
            <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground flex-shrink-0" title={`${(selectedAgent.activeLeadsCount || 0).toLocaleString()} assigned`}>
              <Users className="h-3 w-3" />
              {fmtCompact(selectedAgent.activeLeadsCount)}
            </span>
          </div>
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
        <ChevronDown className={cn("h-4 w-4 opacity-50 flex-shrink-0 ml-2 transition-transform", open && "rotate-180")} />
      </button>

      {/* Dropdown — rendered inline (NOT portaled) so it lives inside the parent
          modal's scroll-allowed DOM and its list scrolls with the wheel/touch.
          Opens upward because the trigger sits in the modal footer.

          ⚠️ WIDTH IS NOT INHERITED FROM THE TRIGGER. The panel used left-0 + right-0,
          which made it exactly as wide as the button — and in the modal footer that
          is ~260px. That crushed "Damian Wayne · Gotham Northside" into "Dami… ·
          Gotham No…", so neither the person nor their agency was readable and
          picking the right agent was guesswork. It now takes the room the content
          needs (min-w-full so it never renders narrower than the trigger) and is
          capped so it can never run off a phone screen. */}
      {open && (
        <div ref={panelRef} className={`agent-dd-panel absolute z-50 left-0 ${openDirection === 'down' ? 'top-[calc(100%+4px)]' : 'bottom-[calc(100%+4px)]'} min-w-full w-[360px] max-w-[calc(100vw-2rem)] rounded-md border border-border bg-popover text-popover-foreground shadow-lg flex flex-col max-h-[min(320px,60vh)] overflow-hidden`}>
          {/* Fixed search header */}
          <div className="flex-shrink-0 p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
              {/* type="text" so the app's 16px touch-input rule applies (globals.css) — under 16px
                  iPad Safari zooms the page when it's tapped. */}
              <input
                ref={searchRef}
                type="text"
                value={agentSearch}
                onChange={(e) => setAgentSearch(e.target.value)}
                placeholder="Search agent..."
                className="w-full h-8 pl-7 pr-2 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>

          {/* Scrollable list */}
          <div className="flex-1 min-h-0 overflow-y-auto p-1 overscroll-contain">
            {/* Clear row — only when the caller opts in. Sits above the search
                results and is never filtered out, so "assign to nobody" stays
                reachable no matter what has been typed. */}
            {clearLabel && (
              <button
                type="button"
                onClick={() => { onValueChange?.(""); close(); }}
                className={cn(
                  "w-full text-left rounded-sm px-2 py-2 text-sm transition-colors hover:bg-accent cursor-pointer",
                  !value && "bg-accent font-medium"
                )}
              >
                {!value && <Check className="inline h-3.5 w-3.5 text-primary mr-1.5" />}
                {clearLabel}
              </button>
            )}
            {filteredAgents.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No agents match “{agentSearch}”
              </div>
            ) : filteredAgents.map((agent: AgentWorkloadResponse['agents'][number]) => {
              const UNLIMITED_THRESHOLD = 1000000;
              const maxLeads = (agent as any).maxLeads || 0;
              const isUnlimited = maxLeads >= UNLIMITED_THRESHOLD;
              const current = agent.activeLeadsCount || 0;
              const owned = (agent as any).ownedLeads ?? 0;
              const atLimit = !isUnlimited && maxLeads > 0 && current >= maxLeads;
              const nearLimit = !isUnlimited && maxLeads > 0 && current >= maxLeads * 0.9;
              const isSelected = agent.agentId === value;

              return (
                <button
                  type="button"
                  key={agent.agentId}
                  disabled={atLimit}
                  onClick={() => {
                    if (atLimit) return;
                    onValueChange?.(agent.agentId);
                    close();
                  }}
                  className={cn(
                    "w-full text-left rounded-sm px-2 py-1.5 transition-colors",
                    atLimit ? "opacity-50 cursor-not-allowed" : "hover:bg-accent cursor-pointer",
                    isSelected && "bg-accent"
                  )}
                >
                  <div className="flex items-center gap-3 w-full">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      atLimit ? "bg-red-100" : "bg-gray-200"
                    }`}>
                      <span className={`text-sm font-medium ${atLimit ? "text-red-600" : "text-gray-700"}`}>
                        {generateInitials(agent.agentName)}
                      </span>
                    </div>
                    <div className="flex flex-col items-start flex-1 min-w-0">
                      <div className="flex items-center gap-2 min-w-0 w-full">
                        {/* The NAME wins any contest for space — it is what you are picking.
                            The agency is context, so it yields first and never takes
                            more than 40%. Previously both carried a bare `truncate`
                            with no basis, so flex shrank them equally. */}
                        <span className={`font-medium truncate min-w-0 flex-1 ${atLimit ? "text-gray-400" : "text-gray-900"}`} title={agent.agentName}>{agent.agentName}</span>
                        {(agent as any).agencyName && (
                          <span className="text-xs text-blue-600 truncate max-w-[40%] flex-shrink" title={(agent as any).agencyName}>{(agent as any).agencyName}</span>
                        )}
                        {isSelected && <Check className="h-3.5 w-3.5 text-primary flex-shrink-0 ml-auto" />}
                      </div>
                      <div className="flex items-center gap-2 w-full mt-1">
                        {isUnlimited ? (
                          <div className="flex items-center gap-1.5 flex-wrap text-xs text-gray-500 w-full">
                            <span className="tabular-nums text-red-600 font-medium" title={`${current.toLocaleString()} assigned to them`}>{fmtCompact(current)} assigned</span>
                            <span className="text-gray-300">·</span>
                            <span className="tabular-nums text-blue-600 font-medium" title={`${owned.toLocaleString()} personal leads they uploaded`}>{fmtCompact(owned)} uploaded</span>
                            <span className="text-gray-300">•</span>
                            <span className="text-green-600 font-medium cursor-help" title="Unlimited — no cap on how many leads can be assigned or uploaded to this agent">∞ Unlimited</span>
                            <span className="text-gray-300">•</span>
                            <span className="cursor-help" title="Conversion rate — % of this agent's assigned leads that are Sold & Approved">{agent.conversionRate}% conv.</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 w-full">
                            <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${
                                  atLimit ? "bg-red-500" : nearLimit ? "bg-amber-500" : "bg-primary"
                                }`}
                                style={{ width: `${Math.min(100, (current / maxLeads) * 100)}%` }}
                              />
                            </div>
                            <span className={`text-[11px] tabular-nums flex-shrink-0 ${
                              atLimit ? "text-red-600 font-semibold" : nearLimit ? "text-amber-600 font-medium" : "text-gray-500"
                            }`}>
                              {fmtCompact(current)}/{fmtCompact(maxLeads)} assigned
                            </span>
                            {owned > 0 && (
                              <span className="text-[11px] tabular-nums flex-shrink-0 text-blue-600 font-medium" title={`${owned.toLocaleString()} personal leads they uploaded`}>
                                · {fmtCompact(owned)} uploaded
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default AgentAssignmentDropdown;
